import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import memoize from 'memoize-one';
import { ComponentDataTable, ButtonTooltip, Icon, SimpleCenteredModal, DefaultModal } from '../../../components';
import { sendAlert } from '../../../actions/utils';
import { debounceIndexAttendanceLogRequest, resendAttendanceLogRequest } from '../../../requests/attendanceLogs';
import AttendanceLogShow from './AttendanceLogShow';
import AttendanceLogsFilter from './AttendanceLogsFilter';


const columns = memoize((clickHandler) => [
  {
    name: 'EMPRESA',
    selector: 'company',
    cell: item => item.company.business_name,

    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: 'L. DE P. DE SERVICIOS',
    selector: 'branch_office',
    cell: item => item?.branch_office?.name,
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: 'TRABAJADOR',
    selector: 'employee',
    cell: item => item?.employee?.full_name,
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: 'RUT',
    selector: 'employee',
    cell: item => item?.employee.national_identification,
    sortable: true,
    grow: '1',
    center: true
  },

  {
    name: 'Marcación',
    selector: 'date',
    cell: item => `${item?.request_payload.date ? new Date(item?.request_payload.date).toLocaleDateString('es-CL') : ''} ${item?.request_payload.time}`,
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: "Fuente",
    selector: "source",
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: "Estado",
    selector: "status",
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: "Mensaje",
    selector: "message",
    sortable: true,
    grow: '1',
    center: true
  },
  {
    name: 'ACCIONES',
    cell: item => (
      <>
        <ButtonTooltip
          onClick={() => clickHandler(item, 'show')}
          variant="circle-info"
          className="btn-circle"
          size="sm"
          text="Ver"
        >
          <Icon icon="eye" />
        </ButtonTooltip>
        {item.can_sync && (
          <ButtonTooltip
            onClick={() => clickHandler(item, 'resend')}
            variant="circle-primary"
            className="btn-circle"
            size="sm"
            text="Reenviar"
          >
            <Icon icon="archive" />
          </ButtonTooltip>
        )}
      </>
    ),
    ignoreRowClick: true,
    allowOverflow: false,
    button: true,
    grow: '1',
    minWidth: '160px'
  }
]);

const AttendanceLogsDataTable = () => {
  const [onRequest, setOnRequest] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [modalItem, setModalItem] = useState();
  const [modalTitle, setModalTitle] = useState('');
  const [modalBody, setModalBody] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [modalAction, setModalAction] = useState();
  const [modalSize, setModalSize] = useState('md');
  const [amount, setAmount] = useState(0);
  const [simpleModalTitle, setSimpleModalTitle] = useState();
  const [simpleModalBody, setSimpleModalBody] = useState(<></>);
  const [simpleModalShow, setSimpleModalShow] = useState();
  const [moreData, setMoreData] = useState(false);
  const [query, setQuery] = useState();
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);

  const dispatch = useDispatch();

  const handleSuccessIndex = response => {
    const { data, metadata } = response.data;
    setEmployees(data);
    setAmount(metadata.amount);
  };

  const handleRequest = async params => {
    const queryParams = {
      ...params,
      ...query
    };

    setOnRequest(true);
    debounceIndexAttendanceLogRequest({
      dispatch,
      params: queryParams,
      successCallback: handleSuccessIndex,
      callback: () => setOnRequest(false)
    });
  };

  const handleShow = data => {
    setSimpleModalTitle(`Detalle de Marcación`);
    setSimpleModalBody(<AttendanceLogShow data={data} />);
    setSimpleModalShow(true);
  };


  const handleResend = async ids => {
    setOnRequest(true);
    resendAttendanceLogRequest({
      dispatch,
      params: { attendance_log_ids: ids },
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Acción realizada con éxito' }));
        setModalShow(false);
      },
      failureCallback: () => {
        dispatch(sendAlert({ kind: 'error', message: 'Error al realizar la acción' }));
        setModalShow(false);
      },
      callback: () => setMoreData(!moreData)
    });
  };

  const handleActions = (item, action) => {
    switch (action) {
      case 'show':
        setModalSize('lg');
        handleShow(item);
        break;
      case 'resend':
        setModalItem([item.id]);
        setModalSize('md');
        setModalTitle('Reenviar Log');
        setModalBody('¿Esta seguro que desea reenviar este log?');
        setModalShow(true);
        setModalAction(() => handleResend);
        break;

      default:
        // eslint-disable-next-line no-console
        console.warn('Error: Action not found');
        break;
    }
  };

  const sortColumnCase = name => {
    switch (name) {
      case 'company':
        return { sort_company: name };
      case 'branch_office':
        return { sort_branch_office: name };
      default:
        return { sort_column: name };
    }
  };


  const formRequest = values => {
    setQuery({ by_range: values.rangeDate, by_company: values.company_id, by_employee: values.employee_id });
    setMoreData(!moreData);
  };

  const handleChangeSync = () => {
    const ids = selectedRows.reduce((acc, current) => {
      acc.push(current.id);
      return acc;
    }, []);
    setModalItem(ids);
    setModalSize('md');
    setModalTitle('Reenviar Logs');
    setModalBody('¿Esta seguro que desea reenviar los logs seleccionados?');
    setModalShow(true);
    setModalAction(() => handleResend);
  };

  const handleSelectedRows = item => {
    setSelectedRows(item.selectedRows);
    setSelectedCount(item.selectedCount);
  };

  return (
    <>
      <div>
        <AttendanceLogsFilter formRequest={formRequest} />

        <ComponentDataTable
          onRequest={onRequest}
          columns={columns(handleActions)}
          data={employees}
          moreData={moreData}
          totalRows={amount}
          handleSortCase={sortColumnCase}
          resourceRequest={handleRequest}
          pointerOnHover
          withMassActions
          selectableRows
          onSelectedRowsChange={handleSelectedRows}
          massActions={
            <ButtonTooltip
              variant="circle-primary"
              className="btn-circle"
              text="Reenviar Logs"
              disabled={!selectedCount || onRequest}
              onClick={() => handleChangeSync()}
            >
              <Icon icon="archive" />
            </ButtonTooltip>
          }
        />
        <DefaultModal
          title={modalTitle}
          body={modalBody}
          show={modalShow}
          handleClose={() => setModalShow(false)}
          handleConfirm={() => modalAction(modalItem)}
          disabled={onRequest}
          titleBtnClose="Cancelar"
          titleBtnSave="Confirmar"
        />
        <SimpleCenteredModal
          title={simpleModalTitle}
          body={simpleModalBody}
          show={simpleModalShow}
          size={modalSize}
          onHide={() => setSimpleModalShow(false)}
        />
      </div>
    </>
  );
};

export default AttendanceLogsDataTable;
